import { useEffect, useState, type ReactNode } from "react"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { cn } from "@/primitives/utils"
import { useViewport } from "./viewport.tsx"

/*
 * What a page looks like while its data loads: grey blocks at the real geometry of what will
 * replace them, under the PageHeader, which renders at once with its real crumbs. Nothing says
 * "One moment…".
 *
 * It waits 150ms before showing, so a load that is quick anyway never flashes.
 */

/** How long a load runs before its skeleton shows. */
const showAfter = 150

/**
 * Whether a skeleton that mounted `ms` ago should show yet: false until then, so a load that is
 * quick anyway never flashes grey. A skeleton that holds its place while hidden (a section's, a
 * card grid's) stays at its real size and only turns visible, so nothing moves when it does.
 */
export function useShownAfter(ms: number = showAfter) {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setShown(true), ms)
    return () => clearTimeout(timer)
  }, [ms])
  return shown
}

/** Row title widths, fixed so the skeleton reads as varied text yet never moves between renders. */
const titleWidths = [
  "w-31/50",
  "w-12/25",
  "w-7/10",
  "w-11/20",
  "w-2/5",
  "w-33/50",
  "w-13/25",
  "w-11/25",
] as const

/** Cards per column on a Board skeleton, left to right. */
const boardCards = [3, 2, 3, 1] as const

function ListRows() {
  return titleWidths.map((width) => (
    <div key={width} className="flex h-9 items-center gap-3 border-b border-border-subtle px-6">
      <Skeleton className="size-4 rounded-full" />
      <Skeleton className="h-3 w-12" />
      <Skeleton className={cn("h-3", width)} />
    </div>
  ))
}

function Board() {
  return (
    <div className="flex gap-2 px-6 py-4">
      {boardCards.map((cards, column) => (
        <div
          key={column}
          className="flex w-64 shrink-0 flex-col gap-1.5 rounded-lg bg-surface-sunken p-1.5"
        >
          <div className="flex h-8 items-center gap-2 px-1.5">
            <Skeleton className="size-3.5 rounded-full" />
            <Skeleton className="h-3 w-20" />
          </div>
          {Array.from({ length: cards }, (_, card) => (
            <Skeleton key={card} className="h-16 bg-card ring-1 ring-border" />
          ))}
        </div>
      ))}
    </div>
  )
}

function Document() {
  return (
    <div className="flex min-h-full">
      <div className="min-w-0 flex-1">
        <div className="mx-auto max-w-3xl space-y-4 px-10 pt-8">
          <Skeleton className="h-8 w-2/3" />
          <div className="space-y-2.5 pt-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-11/12" />
            <Skeleton className="h-3.5 w-3/5" />
          </div>
        </div>
      </div>
      <div className="w-72 shrink-0 space-y-0.5 border-l border-border-subtle px-4 py-6">
        {titleWidths.slice(0, 6).map((width) => (
          <div key={width} className="flex h-8 items-center gap-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className={cn("h-3", width)} />
          </div>
        ))}
      </div>
    </div>
  )
}

function Settings() {
  return (
    <div className="mx-auto max-w-2xl space-y-10 px-10 py-8">
      {[0, 1, 2].map((panel) => (
        <div key={panel}>
          <Skeleton className="mb-3 h-4 w-32" />
          <div className="divide-y divide-border-subtle rounded-lg bg-card ring-1 ring-border">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex h-12 items-center gap-6 px-4">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="ml-auto h-7 w-40" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function Dashboard() {
  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-8 py-6">
      <div className="grid grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((tile) => (
          <Skeleton key={tile} className="h-28 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-lg" />
    </div>
  )
}

function MasterRows() {
  return titleWidths.slice(0, 5).map((width) => (
    <div key={width} className="flex h-12 items-center gap-3 px-4">
      <Skeleton className="size-2 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className={cn("h-3", width)} />
        <Skeleton className="h-2.5 w-16" />
      </div>
    </div>
  ))
}

/**
 * A split page's chosen item, laid out as its detail is (a group's): its name over a count, then a
 * panel of rows, each a face and a name.
 */
function SplitMain() {
  return (
    <div className="max-w-3xl px-10 py-8 compact:px-4 compact:py-6">
      <div className="space-y-2.5">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-3 w-20" />
      </div>
      <div className="mt-6 divide-y divide-border-subtle rounded-lg bg-card ring-1 ring-border">
        {titleWidths.slice(0, 4).map((width) => (
          <div key={width} className="flex h-12 items-center gap-3 px-4">
            <Skeleton className="size-6 rounded-full" />
            <Skeleton className={cn("h-3", width)} />
          </div>
        ))}
      </div>
    </div>
  )
}

function DialogRows() {
  return titleWidths.slice(0, 3).map((width) => (
    <div key={width} className="flex h-8 items-center gap-2 px-2">
      <Skeleton className="size-4" />
      <Skeleton className={cn("h-3", width)} />
    </div>
  ))
}

const kinds = {
  list: ListRows,
  board: Board,
  document: Document,
  settings: Settings,
  dashboard: Dashboard,
  "split-master": MasterRows,
  "split-main": SplitMain,
  "dialog-list": DialogRows,
} as const

/*
 * The same kinds at the compact layout's geometry, as PageBody and PagePane lay a page out there:
 * 16px gutters, rows of two lines, one Status of a Board under its switcher, a ticket's properties
 * as chips under its title rather than a rail beside it, settings rows with the label over the
 * control, and two tiles to a row. Every one but the full-bleed `list` and `board` leaves the
 * gutters to the body it sits in, as the page's own content does.
 */

function CompactListRows() {
  return titleWidths.map((width) => (
    <div
      key={width}
      className="flex min-h-14 flex-col justify-center gap-2 border-b border-border-subtle px-4"
    >
      <div className="flex items-center gap-3">
        <Skeleton className="size-4 rounded-full" />
        <Skeleton className={cn("h-3.5", width)} />
      </div>
      <Skeleton className="ml-7 h-2.5 w-28" />
    </div>
  ))
}

/**
 * One Status of a Board under its switcher, as the pager draws it: the switcher's 48px row of
 * Statuses, then full-width cards at the height of a card with a one-line title, the pager's
 * commonest (`board-card.tsx`), taller on touch, where that line is.
 */
function CompactBoard() {
  return (
    <div>
      <div className="flex h-12 items-center gap-1 overflow-hidden border-b border-border-subtle px-4">
        {["w-32", "w-28", "w-24"].map((width) => (
          <Skeleton key={width} className={cn("h-9 shrink-0 rounded-md", width)} />
        ))}
      </div>
      <div className="flex flex-col gap-2 px-4 py-3">
        {[0, 1, 2, 3].map((card) => (
          <Skeleton key={card} className="h-23 bg-card ring-1 ring-border touch:h-23.5" />
        ))}
      </div>
    </div>
  )
}

function CompactDocument() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-7 w-4/5" />
      <div className="flex flex-wrap gap-1.5">
        {["w-24", "w-20", "w-16", "w-24"].map((width, chip) => (
          <Skeleton key={chip} className={cn("h-7 rounded-md", width)} />
        ))}
      </div>
      <div className="space-y-2.5 pt-2">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-11/12" />
        <Skeleton className="h-3.5 w-3/5" />
      </div>
    </div>
  )
}

function CompactSettings() {
  return (
    <div className="space-y-8">
      {[0, 1, 2].map((panel) => (
        <div key={panel}>
          <Skeleton className="mb-3 h-4 w-32" />
          <div className="divide-y divide-border-subtle rounded-lg bg-card ring-1 ring-border">
            {[0, 1].map((row) => (
              <div key={row} className="space-y-2 p-4">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function CompactDashboard() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((tile) => (
          <Skeleton key={tile} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-56 rounded-lg" />
    </div>
  )
}

/**
 * A settings page's chip bar while its sections load: 48px, as the AnchorNav's, so nothing moves
 * as it arrives. It holds its place from the start and shows its chips once `shown`.
 */
function CompactAnchorNav({ shown }: { shown: boolean }) {
  return (
    <div
      role="status"
      aria-busy
      aria-label="Loading"
      className="flex h-12 items-center gap-1.5 overflow-hidden border-b border-border-subtle bg-background px-4"
    >
      {shown &&
        ["w-20", "w-18", "w-20", "w-16", "w-16"].map((width, chip) => (
          <Skeleton
            key={chip}
            className={cn("h-8 shrink-0 animate-in rounded-full duration-150 fade-in", width)}
          />
        ))}
    </div>
  )
}

const compactKinds: Record<keyof typeof kinds, () => ReactNode> = {
  list: CompactListRows,
  board: CompactBoard,
  document: CompactDocument,
  settings: CompactSettings,
  dashboard: CompactDashboard,
  "split-master": MasterRows,
  "split-main": SplitMain,
  "dialog-list": DialogRows,
}

/**
 * The skeleton for one kind of page or part: `list` rows, a `board`'s columns, a `document` with
 * its rail, `settings` panels, a `dashboard`'s tiles and chart, the `split-master` list beside a
 * detail, the `split-main` detail beside it, or a `dialog-list` inside a dialog; on `compact`, each in its compact page's shape.
 * `anchor-nav` is a settings page's `nav` pane: nothing on the desk, where the pane's width holds
 * its place, and its chip bar on `compact`.
 */
export function PageSkeleton({ kind }: { kind: keyof typeof kinds | "anchor-nav" }) {
  const shown = useShownAfter()
  const { compact } = useViewport()
  if (kind === "anchor-nav") return compact ? <CompactAnchorNav shown={shown} /> : null
  const Shape = (compact ? compactKinds : kinds)[kind]
  return (
    <div role="status" aria-busy aria-label="Loading">
      {shown && (
        <div className="animate-in duration-150 fade-in">
          <Shape />
        </div>
      )}
    </div>
  )
}
